import type { ContractKind, ContractResponse, DeltaResponse } from '../../api';
import type { ColumnFilterOption, ColumnFiltersState, SortingState } from '../../components';
import { APP_LOCALE, formatInteger, stripCpf } from '../../format';
import { matchesCpf, matchesName, scheduledByCpf, scheduledChanges } from '../shared/roster';
import { KIND_LABELS } from './onboarding';
import { SEGURADOS_COPY, contractOptionLabel } from './segurados';
import type { SeguradoEntry, SeguradoRow } from './SeguradosTable';

/**
 * The filters and the sort of the Segurados table's columns on both panels
 * (#429, #441), in one place so that the company page and the group page
 * cannot come to disagree about who "Saúde" keeps.
 *
 * The table holds what was typed and chosen in its filter row and which header
 * was clicked (`DataTable`'s column filters and sorting); this module turns
 * that into the people on screen. Everything here works on what one population
 * read has already brought: the roster is encrypted at rest, so the server
 * could not search it anyway, and a filter that asked it again would undo the
 * one-request panel. None of it goes near the address bar.
 *
 * Most filters are about one contract, not one person -- a person can be under
 * vida at one company and saúde at another -- so they are tested entry by
 * entry. A person is kept when at least one entry passes all of them, and the
 * row then speaks only for the entries that passed (`entriesInView`). The name,
 * the CPF and the scheduled marker are the person's, so those are tested on the
 * person -- the marker against the same index the marker column reads.
 */

/** "No narrowing" for the entry-level filters below. */
export const ALL = 'all';

export const SCHEDULED_CHOICES = { all: ALL, with: 'with', without: 'without' } as const;
export type ScheduledChoice = (typeof SCHEDULED_CHOICES)[keyof typeof SCHEDULED_CHOICES];

export const SEGURADOS_PAGE_SIZE = 50;

/** The table's column keys, which are also the ids its filters and sort carry. */
export const SEGURADOS_COLUMNS = {
  name: 'full_name',
  cpf: 'cpf',
  birthDate: 'birth_date',
  company: 'company',
  salary: 'salary',
  coverageFrom: 'coverage_from',
  coverageTo: 'coverage_to',
  scheduled: 'scheduled',
  contracts: 'contracts',
} as const;

export interface SeguradosFilter {
  /** Part of the name, accents and case aside. */
  readonly name?: string;
  /** CPF digits, punctuation aside. */
  readonly cpf?: string;
  /** One contract's id, or `ALL`. The company page only. */
  readonly contractId?: string;
  readonly kind?: ContractKind | typeof ALL;
  /** One member company's id, or `ALL`. The group page only. */
  readonly companyId?: string;
  /** Changes asked for and not yet happened, by bare CPF. The company page only. */
  readonly scheduled?: {
    readonly choice: ScheduledChoice;
    readonly index: ReadonlyMap<string, readonly DeltaResponse[]>;
  };
}

// --- The Contratos filter ----------------------------------------------------

/**
 * The Contratos column offers kinds and, on the company page, single contracts
 * in one select, so each value says which of the two it is.
 */
const KIND_PREFIX = 'tipo:';
const CONTRACT_PREFIX = 'contrato:';

/** Every kind, then every contract of the company when there are any. */
export function contractsFilterOptions(
  contracts: readonly ContractResponse[] = [],
): readonly ColumnFilterOption[] {
  return [
    ...(Object.entries(KIND_LABELS) as [ContractKind, string][]).map(([kind, label]) => ({
      value: `${KIND_PREFIX}${kind}`,
      label,
    })),
    ...contracts.map((contract) => ({
      value: `${CONTRACT_PREFIX}${contract.id}`,
      label: contractOptionLabel(contract),
    })),
  ];
}

export const SCHEDULED_FILTER_OPTIONS: readonly ColumnFilterOption[] = [
  { value: SCHEDULED_CHOICES.with, label: SEGURADOS_COPY.withScheduled },
  { value: SCHEDULED_CHOICES.without, label: SEGURADOS_COPY.withoutScheduled },
];

function filterValue(filters: ColumnFiltersState, id: string): string {
  return filters.find((entry) => entry.id === id)?.value ?? '';
}

/**
 * What the table's filter row asks for. On the company page pass the
 * scheduled-change index -- built from the contracts this same filter keeps,
 * see `scheduledInView` -- so the Movimentação agendada filter reads the
 * markers the column shows.
 */
export function seguradosFilterFrom(
  filters: ColumnFiltersState,
  scheduledIndex?: ReadonlyMap<string, readonly DeltaResponse[]>,
): SeguradosFilter {
  const contracts = filterValue(filters, SEGURADOS_COLUMNS.contracts);
  const choice = filterValue(filters, SEGURADOS_COLUMNS.scheduled);
  return {
    name: filterValue(filters, SEGURADOS_COLUMNS.name),
    cpf: filterValue(filters, SEGURADOS_COLUMNS.cpf),
    kind: contracts.startsWith(KIND_PREFIX)
      ? (contracts.slice(KIND_PREFIX.length) as ContractKind)
      : ALL,
    contractId: contracts.startsWith(CONTRACT_PREFIX)
      ? contracts.slice(CONTRACT_PREFIX.length)
      : ALL,
    companyId: filterValue(filters, SEGURADOS_COLUMNS.company) || ALL,
    ...(scheduledIndex !== undefined && {
      scheduled: {
        choice:
          choice === SCHEDULED_CHOICES.with || choice === SCHEDULED_CHOICES.without
            ? choice
            : SCHEDULED_CHOICES.all,
        index: scheduledIndex,
      },
    }),
  };
}

// --- Filtering ---------------------------------------------------------------

/** What an entry-level filter needs to know about a contract. */
type ContractFacts = Pick<SeguradoEntry, 'contract_id' | 'company_id'> & {
  readonly contract_kind: ContractKind | undefined;
};

/** Whether one contract passes the contract, kind and company filters. */
export function contractPasses(contract: ContractFacts, filter: SeguradosFilter): boolean {
  const { contractId = ALL, kind = ALL, companyId = ALL } = filter;
  return (
    (contractId === ALL || contract.contract_id === contractId) &&
    (kind === ALL || contract.contract_kind === kind) &&
    (companyId === ALL || contract.company_id === companyId)
  );
}

/** The entries a row speaks for: the ones that passed every entry-level filter. */
export function entriesInView(
  person: SeguradoRow,
  filter: SeguradosFilter,
): readonly SeguradoEntry[] {
  return person.contracts.filter((entry) => contractPasses(entry, filter));
}

/**
 * The scheduled changes on the contracts the filter keeps, by bare CPF, so
 * that the marker column and the Movimentação agendada filter both speak of
 * the same contracts the rows do.
 */
export function scheduledInView(
  deltas: readonly DeltaResponse[],
  contractsById: ReadonlyMap<string, ContractResponse>,
  filter: SeguradosFilter,
  asOf: string,
): ReadonlyMap<string, readonly DeltaResponse[]> {
  const inView = deltas.filter((delta) =>
    contractPasses(
      { contract_id: delta.contract_id, contract_kind: contractsById.get(delta.contract_id)?.kind },
      filter,
    ),
  );
  return scheduledByCpf(scheduledChanges(inView, asOf));
}

function scheduledPasses(person: SeguradoRow, filter: SeguradosFilter): boolean {
  if (filter.scheduled === undefined || filter.scheduled.choice === SCHEDULED_CHOICES.all) {
    return true;
  }
  const marked = (filter.scheduled.index.get(stripCpf(person.cpf)) ?? []).length > 0;
  return filter.scheduled.choice === SCHEDULED_CHOICES.with ? marked : !marked;
}

/** The people kept by every filter, in the order they arrived. */
export function filterSegurados<TPerson extends SeguradoRow>(
  people: readonly TPerson[],
  filter: SeguradosFilter,
): readonly TPerson[] {
  if (!isNarrowed(filter)) {
    return people;
  }
  const { name = '', cpf = '' } = filter;
  return people.filter(
    (person) =>
      matchesName(person, name) &&
      matchesCpf(person, cpf) &&
      person.contracts.some((entry) => contractPasses(entry, filter)) &&
      scheduledPasses(person, filter),
  );
}

/** Whether the list on screen is narrower than the population: any column filter is set. */
export function isNarrowed(filter: SeguradosFilter): boolean {
  return (
    (filter.name ?? '').trim() !== '' ||
    (filter.cpf ?? '').trim() !== '' ||
    (filter.contractId ?? ALL) !== ALL ||
    (filter.kind ?? ALL) !== ALL ||
    (filter.companyId ?? ALL) !== ALL ||
    (filter.scheduled?.choice ?? ALL) !== ALL
  );
}

// --- Sorting -----------------------------------------------------------------

/**
 * The value a sortable column compares, for the table's header and for
 * `sortSegurados` alike. A per-contract column sorts by the first entry in
 * view, the one whose value the row shows first. `undefined` for a column that
 * does not sort.
 */
export function seguradoSortValue(
  person: SeguradoRow,
  columnId: string,
  inView: (person: SeguradoRow) => readonly SeguradoEntry[],
): string | number | undefined {
  switch (columnId) {
    case SEGURADOS_COLUMNS.name:
      return person.full_name;
    case SEGURADOS_COLUMNS.cpf:
      return stripCpf(person.cpf);
    case SEGURADOS_COLUMNS.birthDate:
      return person.birth_date;
    case SEGURADOS_COLUMNS.salary: {
      const salary = inView(person)[0]?.salary;
      return salary === null || salary === undefined ? undefined : Number(salary);
    }
    case SEGURADOS_COLUMNS.coverageFrom:
      return inView(person)[0]?.coverage_from;
    case SEGURADOS_COLUMNS.coverageTo:
      return inView(person)[0]?.coverage_to ?? undefined;
    default:
      return undefined;
  }
}

function compare(left: string | number, right: string | number): number {
  if (typeof left === 'number' && typeof right === 'number') return left - right;
  return String(left).localeCompare(String(right), APP_LOCALE, {
    numeric: true,
    sensitivity: 'base',
  });
}

/**
 * The people in the order the header asks for; as they arrived when it asks
 * for none. A missing value goes last in both directions, as in `DataTable`.
 */
export function sortSegurados<TPerson extends SeguradoRow>(
  people: readonly TPerson[],
  sorting: SortingState,
  inView: (person: SeguradoRow) => readonly SeguradoEntry[],
): readonly TPerson[] {
  const by = sorting[0];
  if (by === undefined) {
    return people;
  }
  const direction = by.desc ? -1 : 1;
  return people.toSorted((a, b) => {
    const left = seguradoSortValue(a, by.id, inView);
    const right = seguradoSortValue(b, by.id, inView);
    if (left === undefined || right === undefined) {
      return left === right ? 0 : left === undefined ? 1 : -1;
    }
    return direction * compare(left, right);
  });
}

// --- The footnote ------------------------------------------------------------

/**
 * The sentence under the table: the range on screen, how many match, and how
 * many are covered. "1–50 de 312 pessoa(s) que correspondem, de 1.480
 * cobertas." when narrowed, "1–50 de 1.480 pessoa(s)." when not.
 */
export function seguradosFootnote({
  page,
  pageSize,
  matching,
  total,
  narrowed,
}: {
  page: number;
  pageSize: number;
  matching: number;
  total: number;
  narrowed: boolean;
}): string {
  if (matching === 0) {
    return `Nenhuma de ${formatInteger(total)} pessoa(s) cobertas corresponde.`;
  }
  const first = (page - 1) * pageSize + 1;
  const last = Math.min(page * pageSize, matching);
  const range = `${formatInteger(first)}–${formatInteger(last)}`;
  return narrowed
    ? `${range} de ${formatInteger(matching)} pessoa(s) que correspondem, de ${formatInteger(total)} cobertas.`
    : `${range} de ${formatInteger(total)} pessoa(s).`;
}
